import type { PostHog } from "posthog-js";
// Subpath on purpose: the zod-free half of the contract (no zod in the browser).
import {
  POSTHOG_STOREFRONT_CONFIG_GLOBAL,
  POSTHOG_STOREFRONT_EVENTS,
  parsePosthogStorefrontConfig,
  toPosthogIdentity,
  type PosthogStorefrontConfig,
} from "@tripcart/shared/apps/posthog-storefront";

import { NO_REPLAY_ATTRIBUTE } from "@/components/no-replay";
import { scheduleAfterLoadIdle } from "@/lib/after-load-idle";
import { telemetryUrl } from "@/lib/telemetry-url";

/**
 * The agency's own PostHog (the PostHog app), separate from TripCart's
 * platform instance in `posthog-lazy.ts`. posthog-js keeps a named instance's
 * identity and queue apart from the default one, so nothing set here reaches
 * TripCart's analytics (AC-16).
 *
 * The config arrives as an inline script the app writes into the page
 * (`APP_REGISTRY.posthog`), which Next runs `afterInteractive`. Work that
 * starts on mount waits for load + idle before reading it, which also keeps
 * the SDK out of the LCP window; work a guest triggers (a sign-in, a form)
 * comes long after that script ran and loads at once. No config means the app
 * is not installed or has no key: every function below is then a no-op and
 * posthog-js is never downloaded.
 */
const INSTANCE_NAME = "agency";

/** Private layouts (checkout, pay, confirmation, account) render this
 *  wrapper (`components/no-replay.tsx`). Replay never runs on them (AC-17). */
const NO_REPLAY_SELECTOR = `[${NO_REPLAY_ATTRIBUTE}]`;

let loading: Promise<PostHog | null> | null = null;
let replayEnabled = false;

function readConfig(): PosthogStorefrontConfig | null {
  return parsePosthogStorefrontConfig(
    Reflect.get(window, POSTHOG_STOREFRONT_CONFIG_GLOBAL),
  );
}

function syncReplay(instance: PostHog): void {
  const recordable = !document.querySelector(NO_REPLAY_SELECTOR);
  if (replayEnabled && recordable) instance.startSessionRecording();
  else instance.stopSessionRecording();
}

const URL_PROPERTIES = ["$current_url", "$referrer"] as const;

/** Strips credentials (`/claim/<token>`, `?token=`) from URLs before they
 *  leave the browser, the same redaction TripCart's own telemetry uses. */
function redactUrl(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    const url = new URL(value);
    return url.origin + telemetryUrl(url.pathname, url.search);
  } catch {
    return value;
  }
}

function redactEvent<T extends { properties: Record<string, unknown> }>(
  event: T | null,
): T | null {
  if (!event) return null;
  for (const key of URL_PROPERTIES) {
    if (key in event.properties)
      event.properties[key] = redactUrl(event.properties[key]);
  }
  if (typeof event.properties.$pathname === "string")
    event.properties.$pathname = telemetryUrl(event.properties.$pathname, "");
  return event;
}

function initAgency(
  posthog: PostHog,
  config: PosthogStorefrontConfig,
): PostHog | null {
  replayEnabled = config.sessionReplay;
  const instance = posthog.init(
    config.apiKey,
    {
      api_host: config.apiHost,
      // Every storefront shares the *.ontripcart.com parent domain, and the
      // default cookie is set there with the guest's email as distinct_id —
      // readable by any other agency's custom scripts. localStorage is per
      // origin, so the identity stays on this storefront.
      persistence: "localStorage",
      cross_subdomain_cookie: false,
      autocapture: config.autocapture,
      capture_pageview: "history_change",
      capture_pageleave: true,
      rageclick: true,
      // Started per route by `syncReplay`, so a payment page is never recorded.
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
        // Applied while the DOM is captured, so a private page is never
        // recorded even in the moment before `syncReplay` stops the recorder.
        blockSelector: NO_REPLAY_SELECTOR,
        // Explicit false wins over the agency's PostHog project settings,
        // which could otherwise turn on recording of API responses.
        recordBody: false,
        recordHeaders: false,
      },
      capture_performance: false,
      // Off whatever the project settings say: each loads more code from
      // PostHog, and dead clicks record element text.
      disable_surveys: true,
      disable_conversations: true,
      disable_product_tours: true,
      capture_dead_clicks: false,
      enable_heatmaps: false,
      capture_exceptions: false,
      before_send: redactEvent,
      person_profiles: "identified_only",
    },
    INSTANCE_NAME,
  );
  if (!instance) return null;
  syncReplay(instance);
  return instance;
}

/** Sign-in and sign-out navigate right after; a slow SDK download must not
 *  hold the guest on a spinner. */
const NAVIGATION_BUDGET_MS = 1500;

function loadNow(): Promise<PostHog | null> {
  if (loading) return loading;
  const config = readConfig();
  if (!config) return Promise.resolve(null);
  // A failed chunk load must not surface in sign-in or sign-out.
  loading = import("posthog-js")
    .then(({ default: posthog }) => initAgency(posthog, config))
    .catch(() => null);
  return loading;
}

/** For work that starts on mount: waits for load + idle first. */
export function loadAgencyPostHog(): Promise<PostHog | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (loading) return loading;
  return new Promise<void>((resolve) => scheduleAfterLoadIdle(resolve)).then(
    loadNow,
  );
}

function withinBudget(work: Promise<void>): Promise<void> {
  return Promise.race([
    work,
    new Promise<void>((resolve) => setTimeout(resolve, NAVIGATION_BUDGET_MS)),
  ]);
}

/** Called after every route change commits; applies once the SDK has
 *  loaded. */
export function syncAgencyReplay(): void {
  void loading?.then((instance) => instance && syncReplay(instance));
}

type IdentityInput = { email: string; name?: string | null } | null;

async function identifyWith(
  load: () => Promise<PostHog | null>,
  input: IdentityInput,
): Promise<void> {
  const identity = input && toPosthogIdentity(input.email, input.name);
  if (!identity) return;
  const instance = await load();
  if (!instance || !readConfig()?.identifyVisitors) return;
  instance.identify(identity.email, { ...identity });
}

/** Links this browser's visits to a guest by their lowercased email, after a
 *  guest action (checkout contact step, form send). Null is a no-op. */
export function identifyAgencyVisitor(input: IdentityInput): Promise<void> {
  return identifyWith(loadNow, input);
}

/** The same, from a page that already knows the guest on mount. */
export function identifyAgencyVisitorOnLoad(
  input: IdentityInput,
): Promise<void> {
  return identifyWith(loadAgencyPostHog, input);
}

/** Sign-out: the next person on this browser starts anonymous (AC-14). */
export function resetAgencyVisitor(): Promise<void> {
  return withinBudget(loadNow().then((instance) => instance?.reset()));
}

async function captureWith(
  load: () => Promise<PostHog | null>,
  event: string,
  properties?: Record<string, string>,
): Promise<void> {
  const instance = await load();
  // Sent at once: sign-in and invoice both navigate away right after.
  instance?.capture(event, properties, { send_instantly: true });
}

export function trackAgencySignIn(email: string): Promise<void> {
  return withinBudget(
    identifyAgencyVisitor({ email }).then(() =>
      captureWith(loadNow, POSTHOG_STOREFRONT_EVENTS.signedIn),
    ),
  );
}

/** After the "Ask a question" form sends: who asked, then the moment. */
export function trackAgencyQuestionSent(input: IdentityInput): Promise<void> {
  return withinBudget(
    identifyAgencyVisitor(input).then(() =>
      captureWith(loadNow, POSTHOG_STOREFRONT_EVENTS.questionSent),
    ),
  );
}

export function trackAgencyInvoiceOpened(): void {
  void captureWith(loadNow, POSTHOG_STOREFRONT_EVENTS.invoiceOpened);
}

/** Once per booking per tab: reloading the confirmation page is not a second
 *  booking. Storage can throw (private mode); the event still goes once per
 *  page view then. */
export function trackAgencyBookingCompleted(bookingRef: string): void {
  const key = `tc:ph:booked:${bookingRef}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Storage unavailable — fall through and send.
  }
  void captureWith(
    loadAgencyPostHog,
    POSTHOG_STOREFRONT_EVENTS.bookingCompleted,
    { bookingRef },
  );
}
